import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Calculator, BookOpen, FlaskConical, Landmark, BookHeart, Languages,
  Flame, Target, CalendarDays, CheckCircle2, Sparkles, Camera, MessageCircle, FileText, ArrowRight,
} from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import TargetTrackingCard from "@/components/TargetTrackingCard";

const ICON = { Calculator, BookOpen, FlaskConical, Landmark, BookHeart, Languages };
const ACCENT = {
  matematik: "bg-blue-50 text-blue-700 border-blue-100",
  turkce: "bg-orange-50 text-orange-700 border-orange-100",
  fen: "bg-green-50 text-green-700 border-green-100",
  inkilap: "bg-yellow-50 text-yellow-800 border-yellow-100",
  din: "bg-purple-50 text-purple-700 border-purple-100",
  ingilizce: "bg-pink-50 text-pink-700 border-pink-100",
};

export default function StudentDashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [subjects, setSubjects] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/progress/summary").then((r) => r.data),
      api.get("/curriculum/subjects").then((r) => r.data),
    ]).then(([s, sub]) => {
      setSummary(s);
      setSubjects(sub);
    });
  }, []);

  const greet = () => {
    const h = new Date().getHours();
    if (h < 11) return "Günaydın";
    if (h < 18) return "İyi günler";
    return "İyi akşamlar";
  };

  return (
    <div className="space-y-6 ela-fade-in" data-testid="student-dashboard">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm text-slate-500">{greet()},</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            {user?.full_name} 👋
          </h1>
          <p className="text-slate-500 text-sm mt-1">Bismillah, bugün de güzel bir çalışma günü.</p>
        </div>
        <Link to="/koc" data-testid="dashboard-koc-link"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-medium hover:bg-slate-800">
          <Sparkles className="w-4 h-4" /> LGS Koçundan Öneri Al
        </Link>
      </div>

      {/* Bugün Banner + Hedef Takip */}
      <div className="grid md:grid-cols-2 gap-3">
        <Link to="/bugun" data-testid="today-banner"
          className="block rounded-2xl border border-slate-900 bg-slate-900 text-white p-5 hover:bg-slate-800 transition group">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#FF6B6B] flex items-center justify-center shrink-0">
              <Sparkles className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-rose-200 font-semibold">AI Koçundan</div>
              <h2 className="font-heading text-lg sm:text-xl font-bold mt-0.5">Bugün ne çalışayım?</h2>
              <p className="text-xs text-slate-300 mt-1">Eksik konularına göre günlük programını şimdi gör.</p>
            </div>
            <ArrowRight className="w-5 h-5 text-rose-200 group-hover:translate-x-1 transition" />
          </div>
        </Link>
        <TargetTrackingCard />
      </div>

      {/* Hero stats — LGS countdown + streak */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard testid="stat-lgs-countdown" label="LGS'ye Kalan" icon={CalendarDays}
          value={summary ? `${summary.days_until_lgs}` : "—"} suffix="gün" accent="rose" />
        <StatCard testid="stat-streak" label="Çalışma Serisi" icon={Flame}
          value={summary ? summary.streak_days : "—"} suffix="gün" accent="orange" />
        <StatCard testid="stat-questions" label="Çözülen Soru" icon={Target}
          value={summary ? summary.total_questions : "—"} accent="blue" />
        <StatCard testid="stat-topics" label="Tamamlanan Konu" icon={CheckCircle2}
          value={summary ? summary.completed_topics : "—"} accent="green" />
      </div>

      {/* 2-col: weekly chart + quick actions */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="md:col-span-2 p-5 rounded-2xl border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-heading font-semibold text-slate-900">Bu Hafta</h3>
              <p className="text-xs text-slate-500">Günlük çözdüğün soru sayısı</p>
            </div>
            <div className="text-right">
              <div className="font-display font-semibold text-2xl text-slate-900">
                {summary?.week_questions ?? 0}
              </div>
              <div className="text-[11px] text-slate-500">soru / {summary?.week_minutes ?? 0} dk</div>
            </div>
          </div>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={summary?.daily_chart || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748b" }}
                  tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 10, fill: "#64748b" }} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Line type="monotone" dataKey="questions" stroke="#FF6B6B" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 rounded-2xl border-slate-200 space-y-3">
          <h3 className="font-heading font-semibold text-slate-900">Hızlı Başla</h3>
          <QuickLink to="/ogretmen" icon={MessageCircle} title="AI Öğretmen" sub="Sohbet et, öğren" testid="quick-teacher" />
          <QuickLink to="/cozum-analiz" icon={Camera} title="Çözümümü İncele" sub="Fotoğraf yükle" testid="quick-solve" />
          <QuickLink to="/denemeler" icon={FileText} title="Mini Deneme" sub="LGS tarzı sorular" testid="quick-exam" />
        </Card>
      </div>

      {/* Doğruluk ve haftalık ders breakdown */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="p-5 rounded-2xl border-slate-200">
          <h3 className="font-heading font-semibold text-slate-900">Genel Doğruluk</h3>
          <div className="flex items-end gap-2 mt-2">
            <span className="font-display text-4xl font-bold text-slate-900">{summary?.accuracy ?? 0}</span>
            <span className="text-slate-500 mb-1">%</span>
          </div>
          <Progress value={summary?.accuracy ?? 0} className="mt-3 h-2" />
          <p className="text-xs text-slate-500 mt-2">
            {summary?.total_correct ?? 0} / {summary?.total_questions ?? 0} doğru
          </p>
        </Card>

        <Card className="md:col-span-2 p-5 rounded-2xl border-slate-200">
          <h3 className="font-heading font-semibold text-slate-900 mb-3">Ders Bazında</h3>
          {(summary?.subject_breakdown?.length ?? 0) === 0 ? (
            <p className="text-sm text-slate-500">Henüz quiz çözmedin. Bir konuyla başla 🎯</p>
          ) : (
            <div className="space-y-2.5">
              {summary.subject_breakdown.map((s) => (
                <div key={s.subject_key} className="flex items-center gap-3">
                  <span className="text-xs font-medium text-slate-700 w-24 shrink-0">{s.subject_name}</span>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#FF6B6B]" style={{ width: `${s.accuracy}%` }} />
                  </div>
                  <span className="text-xs font-display text-slate-600 w-10 text-right">{s.accuracy}%</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Dersler grid */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-xl font-bold text-slate-900">Derslerin</h2>
          <Link to="/dersler" className="text-sm text-[#FF6B6B] font-medium" data-testid="see-all-subjects">Tümü →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {subjects.map((s) => {
            const Icon = ICON[s.icon] || BookOpen;
            return (
              <Link key={s.key} to={`/dersler/${s.key}`}
                data-testid={`subject-card-${s.key}`}
                className={`ela-card-hover rounded-2xl p-4 border ${ACCENT[s.key] || "bg-slate-50 text-slate-700 border-slate-100"}`}>
                <Icon className="w-6 h-6 mb-2" />
                <div className="font-heading font-semibold text-slate-900">{s.name}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{s.topic_count} konu</div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Zayıf konular */}
      {summary?.weak_topics?.length > 0 && (
        <Card className="p-5 rounded-2xl border-slate-200">
          <h3 className="font-heading font-semibold text-slate-900 mb-3">Güçlendirmen Gereken Konular</h3>
          <div className="space-y-2">
            {summary.weak_topics.map((w) => (
              <Link key={w.topic_code}
                to={`/konu/${w.subject_key}/${w.topic_code}`}
                className="flex items-center justify-between p-3 rounded-xl border border-slate-200 hover:border-[#FF6B6B] hover:bg-rose-50/30 transition">
                <div>
                  <div className="text-sm font-medium text-slate-900">{w.topic_name}</div>
                  <div className="text-xs text-slate-500">{w.subject_name} · %{w.accuracy} doğruluk</div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </Link>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function StatCard({ label, icon: Icon, value, suffix, accent, testid }) {
  const colors = {
    rose: "bg-rose-50 text-rose-600",
    orange: "bg-orange-50 text-orange-600",
    blue: "bg-blue-50 text-blue-600",
    green: "bg-green-50 text-green-600",
  };
  return (
    <div data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${colors[accent]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className="flex items-baseline gap-1">
        <span className="font-display text-2xl font-bold text-slate-900">{value}</span>
        {suffix && <span className="text-xs text-slate-500">{suffix}</span>}
      </div>
    </div>
  );
}

function QuickLink({ to, icon: Icon, title, sub, testid }) {
  return (
    <Link to={to} data-testid={testid}
      className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 border border-transparent hover:border-slate-200 transition group">
      <div className="w-9 h-9 rounded-xl bg-rose-50 text-[#FF6B6B] flex items-center justify-center">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-900">{title}</div>
        <div className="text-[11px] text-slate-500">{sub}</div>
      </div>
      <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-0.5 transition" />
    </Link>
  );
}
